/**
 * An in-memory log of everything the app reports to Mixpanel and to the ServiceLayer.
 *
 * This exists so that landing-page and funnel work can be VERIFIED rather than assumed.
 * Both rails are fire-and-forget by design -- a failed beacon must never surface to the
 * member -- which means the normal signal that telemetry is broken is a chart that is
 * quietly wrong weeks later. The panel makes each event visible at the moment it fires.
 *
 * Never reaches production: see `app/debug/index.js`, where the whole module is behind a
 * statically-false branch in the production build.
 *
 * REDACTION IS NOT OPTIONAL. The ServiceLayer rail carries `/api/refi/auth` bodies, which
 * hold the last four of a Social Security number, a date of birth or an account number,
 * and `/save` bodies, which hold the full applicant record. QA runs against real-shaped
 * data and panels get screenshotted into chat. So sensitive values are replaced with
 * their length before they are ever stored -- not at render time, because anything held
 * in memory can be read out of it. The character count is kept deliberately: it is what
 * you actually need to debug a field ("did the last four arrive?") and it is the same
 * thing the funnel telemetry itself is allowed to report.
 */

const MAX_ENTRIES = 300;

/**
 * Keys whose VALUES must never be shown. Matched case-insensitively against the whole
 * key, and against dotted leaf names, so `buyerInfo.ssn` is caught as well as `ssn`.
 */
const SENSITIVE = [
    'account',
    'accountnumber',
    'dob',
    'driverlicense',
    'licensenumber',
    'password',
    'passcode',
    'secret',
    'ssn',
    'taxid',
    'token',
    'validation'
];

const isSensitive = key => SENSITIVE.includes(String(key).toLowerCase());

/** `'123456789'` -> `'••••••••• (9 chars)'`, so shape survives and the value does not. */
const mask = value => {
    if (value == null || value === '') {
        return '(empty)';
    }

    const length = typeof value === 'string' ? value.length : String(value).length;

    return `${'•'.repeat(Math.min(length, 12))} (${length} chars)`;
};

/**
 * Deep-copies a payload, masking sensitive leaves. Also breaks any live reference to
 * redux or component state, so the panel cannot show a value that has since changed.
 */
export const redact = (value, depth = 0) => {
    if (depth > 8 || value == null) {
        return value ?? null;
    }

    if (Array.isArray(value)) {
        return value.map(item => redact(item, depth + 1));
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    if (typeof value !== 'object') {
        return value;
    }

    const out = {};

    for (const [key, item] of Object.entries(value)) {
        if (isSensitive(key)) {
            /*
             * `validation` on /auth is a JSON STRING containing the answer, so masking
             * the object would not be enough -- it is masked whole, by key.
             */
            out[key] = mask(typeof item === 'object' ? JSON.stringify(item) : item);
        } else {
            out[key] = redact(item, depth + 1);
        }
    }

    return out;
};

let entries = [];
let nextId = 1;
const listeners = new Set();

const notify = () => {
    for (const listener of listeners) {
        listener(entries);
    }
};

/**
 * @param {Object} entry  {rail: 'mixpanel'|'server', name, data, status, ms, failed}
 */
export const record = entry => {
    const stored = {
        ...entry,
        at: new Date(),
        data: redact(entry.data),
        id: nextId++
    };

    // Newest first: the thing you just clicked is the thing you want to read.
    entries = [stored, ...entries].slice(0, MAX_ENTRIES);

    notify();

    return stored;
};

/** Attaches a result to an already-recorded request, in place. */
export const complete = (id, patch) => {
    entries = entries.map(entry => entry.id === id ? {...entry, ...patch} : entry);

    notify();
};

export const getEntries = () => entries;

export const clear = () => {
    entries = [];

    notify();
};

export const subscribe = listener => {
    listeners.add(listener);

    return () => listeners.delete(listener);
};

/** Test seam. */
export const reset = () => {
    entries = [];
    nextId = 1;
    listeners.clear();
};

export const MAX = MAX_ENTRIES;
